import type { Client } from '@/types/client';
import { resolveClientTargetCalories } from '@/utils/client-goals';

export const MEAL_PLAN_MINIMUM_CALORIES_KCAL = 1200;
export const PREGNANT_RECOMMENDED_MINIMUM_CALORIES_KCAL = 1800;

const PREGNANCY_TRIMESTERS = new Set(['first', 'second', 'third']);

export type MealPlanCalorieOverrideValidation =
  | { status: 'ok' }
  | { status: 'warning'; message: string; recommendedCalories: number }
  | { status: 'error'; message: string; minimumCalories: number };

export const parseMealPlanCalories = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizePregnancyValue = (value: unknown): string => {
  const normalized = String(value ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (['first', '1', '1st', 'first_trimester'].includes(normalized)) return 'first';
  if (['second', '2', '2nd', 'second_trimester'].includes(normalized)) return 'second';
  if (['third', '3', '3rd', 'third_trimester'].includes(normalized)) return 'third';
  return normalized;
};

export const isPregnantMealPlanClient = (client?: Pick<Client, 'pregnancyTrimester' | 'pregnancyStatus'> | null): boolean => {
  const trimester = normalizePregnancyValue(client?.pregnancyTrimester);
  if (PREGNANCY_TRIMESTERS.has(trimester)) return true;

  const status = normalizePregnancyValue(client?.pregnancyStatus);
  return status === 'pregnant' || PREGNANCY_TRIMESTERS.has(status);
};

const formatCalories = (value: number): string => String(Math.round(value));

export const validateMealPlanCalorieOverride = (
  client: Pick<Client, 'eer' | 'pregnancyTrimester' | 'pregnancyStatus'> | Client | null | undefined,
  targetCalories: unknown
): MealPlanCalorieOverrideValidation => {
  const calories = parseMealPlanCalories(targetCalories);
  if (calories === null) return { status: 'ok' };

  const isPregnant = isPregnantMealPlanClient(client);

  if (isPregnant && calories <= MEAL_PLAN_MINIMUM_CALORIES_KCAL) {
    return {
      status: 'error',
      minimumCalories: MEAL_PLAN_MINIMUM_CALORIES_KCAL,
      message: 'Meal plans at or below 1200 kcal are not allowed for pregnant clients. Please enter a calorie target above 1200 kcal.',
    };
  }

  if (calories < MEAL_PLAN_MINIMUM_CALORIES_KCAL) {
    return {
      status: 'warning',
      recommendedCalories: MEAL_PLAN_MINIMUM_CALORIES_KCAL,
      message:
        'This calorie target is below 1200 kcal/day. You can still continue and create the meal plan, but review whether this target is clinically appropriate before proceeding.',
    };
  }

  if (isPregnant && calories < PREGNANT_RECOMMENDED_MINIMUM_CALORIES_KCAL) {
    return {
      status: 'warning',
      recommendedCalories: PREGNANT_RECOMMENDED_MINIMUM_CALORIES_KCAL,
      message:
        'This client is pregnant. Based on the client\'s condition, the recommended minimum calorie intake is 1800 kcal/day. You may override this value if clinically appropriate, but it is recommended not to go below 1800 kcal.',
    };
  }

  const calculatedEer = parseMealPlanCalories(resolveClientTargetCalories(client as Client | null | undefined) ?? client?.eer);
  if (!isPregnant && calculatedEer !== null && calories < calculatedEer) {
    return {
      status: 'warning',
      recommendedCalories: calculatedEer,
      message:
        `The recommended calorie target for this client is ${formatCalories(calculatedEer)} kcal/day, calculated using the client's age, gender, height, weight, activity level, and calculation formula. You may override this value if clinically appropriate.`,
    };
  }

  return { status: 'ok' };
};
